/** @jsxImportSource theme-ui */
import React from 'react';
import { SxProp } from 'theme-ui';

import * as dto from 'client/dto';
import theme from 'theme';
import { valignHackTop } from 'utils/cssHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import Typography, { TypographyColors } from 'components/_common/Typography/Typography';
import { BdTooltip } from 'components/_controls/BdTooltip';

import { BdNoDataIcon } from '../BdNoDataIcon';

const CreateSvgPossible = (
  color: keyof typeof TypographyColors,
  backgroundColor: keyof typeof TypographyColors,
  backgroundOpacity = 0.15
) => {
  return (
    <svg
      data-testid="possible"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        opacity={backgroundOpacity.toString()}
        cx="18"
        cy="18"
        r="15"
        fill={theme.colors[backgroundColor]}
      />
      <path
        d="M11.6962 18.0091C13.1321 18.0091 14.2962 16.8152 14.2962 15.3424C14.2962 13.8697 13.1321 12.6758 11.6962 12.6758C10.2603 12.6758 9.09619 13.8697 9.09619 15.3424C9.09619 16.8152 10.2603 18.0091 11.6962 18.0091Z"
        stroke={theme.colors[color]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.186 23.3244C16.186 20.6216 14.1639 18.4189 11.655 18.4189C9.14611 18.4189 7.12402 20.6081 7.12402 23.3244H16.186Z"
        stroke={theme.colors[color]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M24.3861 18.0091C25.8221 18.0091 26.9861 16.8152 26.9861 15.3424C26.9861 13.8697 25.8221 12.6758 24.3861 12.6758C22.9502 12.6758 21.7861 13.8697 21.7861 15.3424C21.7861 16.8152 22.9502 18.0091 24.3861 18.0091Z"
        stroke={theme.colors[color]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.8759 23.3244C28.8759 20.6216 26.8538 18.4189 24.3449 18.4189C21.8361 18.4189 19.814 20.6081 19.814 23.3244H28.8759Z"
        stroke={theme.colors[color]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const CreateSvgNotPossible = (
  color: keyof typeof TypographyColors,
  backgroundColor: keyof typeof TypographyColors,
  lineColor: keyof typeof TypographyColors,
  backgroundOpacity = 0.15
) => {
  return (
    <svg
      data-testid="not-possible"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        opacity={backgroundOpacity.toString()}
        cx="18"
        cy="18"
        r="15"
        fill={theme.colors[backgroundColor]}
      />
      <path
        d="M11.6962 18.0091C13.1321 18.0091 14.2962 16.8152 14.2962 15.3424C14.2962 13.8697 13.1321 12.6758 11.6962 12.6758C10.2603 12.6758 9.09619 13.8697 9.09619 15.3424C9.09619 16.8152 10.2603 18.0091 11.6962 18.0091Z"
        stroke={theme.colors[color]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.186 23.3244C16.186 20.6216 14.1639 18.4189 11.655 18.4189C9.14611 18.4189 7.12402 20.6081 7.12402 23.3244H16.186Z"
        stroke={theme.colors[color]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M24.3861 18.0091C25.8221 18.0091 26.9861 16.8152 26.9861 15.3424C26.9861 13.8697 25.8221 12.6758 24.3861 12.6758C22.9502 12.6758 21.7861 13.8697 21.7861 15.3424C21.7861 16.8152 22.9502 18.0091 24.3861 18.0091Z"
        stroke={theme.colors[color]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.8759 23.3244C28.8759 20.6216 26.8538 18.4189 24.3449 18.4189C21.8361 18.4189 19.814 20.6081 19.814 23.3244H28.8759Z"
        stroke={theme.colors[color]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line x1="7.64645" y1="28.6464" x2="28.6464" y2="7.64645" stroke={theme.colors[lineColor]} />
    </svg>
  );
};

const getIcon = (type: dto.TransmissibilityDisruptionFactorType, dark: boolean) => {
  switch (type) {
    case dto.TransmissibilityDisruptionFactorType.NoData:
      return <BdNoDataIcon dark={dark} />;
    case dto.TransmissibilityDisruptionFactorType.NotPossible:
    case dto.TransmissibilityDisruptionFactorType.NotPossibleUnlessLocally:
      return CreateSvgNotPossible('stone40', 'stone50', dark ? 'stone20' : 'stone70', 0.15);
    case dto.TransmissibilityDisruptionFactorType.Unlikely:
    case dto.TransmissibilityDisruptionFactorType.LimitedViaHumanToHuman:
      return CreateSvgPossible(dark ? 'stone20' : 'stone70', 'stone50', 0.15);
    case dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironmentLocally:
    case dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironment:
      return CreateSvgPossible(dark ? 'starfish40' : 'starfish100', 'starfish100', 0.15);
    case dto.TransmissibilityDisruptionFactorType.PossibleViaHumanToHuman:
      return CreateSvgPossible(dark ? 'starfish40' : 'starfish100', 'starfish100', 0.15);
    default:
      return <BdNoDataIcon dark={dark} />;
  }
};

const getDescription = (type: dto.TransmissibilityDisruptionFactorType) => {
  switch (type) {
    case dto.TransmissibilityDisruptionFactorType.NotPossible:
    case dto.TransmissibilityDisruptionFactorType.NotPossibleUnlessLocally:
      return 'Not possible';
    case dto.TransmissibilityDisruptionFactorType.Unlikely:
      return 'Unlikely (no historic precedent)';
    case dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironmentLocally:
    case dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironment:
      return 'Possible (via environment)';
    case dto.TransmissibilityDisruptionFactorType.PossibleViaHumanToHuman:
      return 'Possible (human-to-human)';
    case dto.TransmissibilityDisruptionFactorType.LimitedViaHumanToHuman:
      return 'Limited (human-to-human)';
    case dto.TransmissibilityDisruptionFactorType.NoData:
    default:
      return 'No data available';
  }
};

const hasDescription = (type: dto.TransmissibilityDisruptionFactorType) => {
  return (
    type === dto.TransmissibilityDisruptionFactorType.NotPossible ||
    type === dto.TransmissibilityDisruptionFactorType.NotPossibleUnlessLocally
  );
};

const hasTooltip = (type: dto.TransmissibilityDisruptionFactorType) => {
  return (
    type === dto.TransmissibilityDisruptionFactorType.NotPossible ||
    type === dto.TransmissibilityDisruptionFactorType.NotPossibleUnlessLocally ||
    type === dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironment
  );
};

type BdTransmissibilitySimpleIconProps = SxProp & {
  type: dto.TransmissibilityDisruptionFactorType;
  labelPosition?: 'top' | 'bottom';
  /** For dark backgrounds */
  dark?: boolean;
  /** For icon only */
  simple?: boolean;
  /** Displays disclaimer for transmissibility on hover instead of always if true */
  tooltip?: boolean;
};

type BdTransmissibilityIconProps = BdTransmissibilitySimpleIconProps & {
  diseaseName: string;
  locationName: string;
  parentLocationName: string;
};

const disclaimerIconStyle = {
  fontSize: '6px',
  width: '6px',
};

const disclaimerTextStyle = {
  maxWidth: '258px',
  ml: '6px',
};

const disclaimerTextFragment = (
  type: dto.TransmissibilityDisruptionFactorType,
  diseaseName: string,
  locationName: string,
  parentLocationName: string
) => {
  switch (type) {
    case dto.TransmissibilityDisruptionFactorType.NotPossible:
      return (
        <>
          {diseaseName} cannot spread from an infected human to other humans or to the environment.
        </>
      );
    case dto.TransmissibilityDisruptionFactorType.NotPossibleUnlessLocally:
      return (
        <>
          {diseaseName} cannot spread from an infected human to other humans or to the environment.
          <span>
            {' '}
            Activity in this location indicates that humans can acquire {diseaseName} locally from
            the environment.
          </span>
        </>
      );
    case dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironment:
      return (
        <>
          Infected individuals in regions or all of {parentLocationName} could spread {diseaseName}{' '}
          to others via the environment. There is no specific data on whether spread can occur in{' '}
          {locationName}.
        </>
      );
    default:
      return <></>;
  }
};

const disclaimer = (
  type: dto.TransmissibilityDisruptionFactorType,
  diseaseName: string,
  locationName: string,
  parentLocationName: string,
  dark: boolean,
  tooltip: boolean
) => (
  <div sx={{ display: 'flex' }}>
    <Typography variant="caption" color={tooltip ? 'white' : dark ? 'deepSea40' : 'stone90'}>
      <BdIcon
        data-testid="disclaimer-icon"
        sx={{ '&.icon.bd-icon.asterisk-icon': disclaimerIconStyle, ...valignHackTop('-4px') }}
        name="icon-asterisk"
        color="sunflower100"
        className="asterisk-icon"
      />
    </Typography>
    <Typography
      data-testid="disclaimer-text"
      variant="caption"
      color={tooltip ? 'white' : dark ? 'deepSea40' : 'stone90'}
      sx={disclaimerTextStyle}
    >
      {disclaimerTextFragment(type, diseaseName, locationName, parentLocationName)}
    </Typography>
  </div>
);

const content = ({ type, dark, labelPosition, simple }: BdTransmissibilitySimpleIconProps) => (
  <div sx={{ display: 'flex' }}>
    {getIcon(type, dark)}
    {!simple && (
      <div
        data-testid="description"
        sx={{
          display: 'flex',
          flexDirection: labelPosition === 'bottom' ? 'column' : 'column-reverse',
          justifyContent: 'space-between',
          ml: '7px',
        }}
      >
        <Typography
          variant="subtitle2"
          color={dark ? 'stone10' : 'stone90'}
          marginBottom={labelPosition === 'bottom' ? '4px' : '0'}
        >
          {getDescription(type)}
          {(type === dto.TransmissibilityDisruptionFactorType.NotPossible ||
            type === dto.TransmissibilityDisruptionFactorType.NotPossibleUnlessLocally ||
            type === dto.TransmissibilityDisruptionFactorType.PossibleViaEnvironment) && (
            <BdIcon
              data-testid="disclaimer-icon"
              sx={{
                '&.icon.bd-icon.asterisk-icon': {
                  ...disclaimerIconStyle,
                  ...valignHackTop('-8px'),
                },
              }}
              name="icon-asterisk"
              color="sunflower100"
              className="asterisk-icon"
            />
          )}
        </Typography>
        <Typography
          variant="caption"
          color={dark ? 'deepSea40' : 'stone90'}
          marginBottom={labelPosition === 'bottom' ? '0' : '4px'}
        >
          Local transmission
        </Typography>
      </div>
    )}
  </div>
);

const BdTransmissibilityIcon: React.FC<BdTransmissibilityIconProps> = ({
  diseaseName,
  locationName,
  parentLocationName,
  type,
  dark = false,
  labelPosition = 'bottom',
  simple = false,
  tooltip = false,
  ...props
}) => {
  return (
    <div data-testid="transmissiblity-icon" sx={{ display: 'flex' }} {...props}>
      {!simple && hasTooltip(type) && tooltip ? (
        <BdTooltip
          customPopup={disclaimer(
            type,
            diseaseName,
            locationName,
            parentLocationName,
            dark,
            tooltip
          )}
          position="right center"
          wide
        >
          <div data-testid="content">{content({ type, dark, labelPosition, simple })}</div>
        </BdTooltip>
      ) : (
        <div data-testid="content">
          {content({ type, dark, labelPosition, simple })}
          {!simple && hasDescription(type) && (
            <div sx={{ mt: '12px' }}>
              {disclaimer(type, diseaseName, locationName, parentLocationName, dark, tooltip)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BdTransmissibilityIcon;
